import { message, Upload } from "antd";
import { requestUploads } from "./api";
import { ImageValidationError, MASTER_IMAGE_MAX_BYTES, validateImage } from "./imageValidation";

export type UploadKind = "brick" | "thumbnail";

export interface SignedUpload {
  key: string;
  url: string;
  fields: Record<string, string>;
}

export const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp";

export const postUpload = (
  upload: SignedUpload,
  file: Blob,
  onProgress?: (percent: number) => void
) => {
  const form = new FormData();
  Object.entries(upload.fields).forEach(([name, value]) => form.append(name, value));
  form.append("file", file);
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", upload.url);
    xhr.upload.onprogress = (ev) => onProgress?.((ev.loaded / ev.total) * 100);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(xhr.responseText || `Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(form);
  });
};

export const uploadImage = async (
  organization: string,
  collection: string,
  kind: UploadKind,
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> => {
  const contentType = await validateImage(file);
  const [upload] = await requestUploads(organization, collection, [{ kind, contentType }]);
  await postUpload(upload, file, onProgress);
  return upload.key;
};

export const uploadBrickRenders = async (
  organization: string,
  collection: string,
  blobs: Blob[]
): Promise<string[]> => {
  const uploads: SignedUpload[] = await requestUploads(
    organization,
    collection,
    blobs.map(() => ({ kind: "brick", contentType: "image/png" }))
  );
  await Promise.all(uploads.map((upload, i) => postUpload(upload, blobs[i])));
  return uploads.map((upload) => upload.key);
};

const rejectIfInvalid = async (file: File, maxBytes?: number) => {
  try {
    await validateImage(file, maxBytes);
    return true;
  } catch (e) {
    if (!(e instanceof ImageValidationError)) throw e;
    message.error(`${file.name}: ${e.message}`);
    return Upload.LIST_IGNORE;
  }
};

// For antd Upload's beforeUpload, which passes the file list as a second
// argument: rejected files never enter the upload list.
export const rejectInvalidImage = (file: File) => rejectIfInvalid(file);

export const rejectInvalidMasterImage = (file: File) =>
  rejectIfInvalid(file, MASTER_IMAGE_MAX_BYTES);
